// Cheque progress, rebuilt (UI rebuild, Astra's `cheque-detail`; behind uiV2(5)). Today's page is screens/McDeal.tsx.
//
// Astra: "Complete the next unfinished cheque stage" — the cheque's figures in one strip (two and one on the phone), then
// the three stages as one timeline: a done stage is one line, the CURRENT stage carries the one gold button that opens its
// form, a stage still to come is folded with "Waiting". The help, the fees, the delivery charge, the notes and Void sit
// under "More". Astra's layout AND quantity (the operator's rule; Screens' audit, 27 Oct 2026): strip, stages and the
// gold button on one phone screen.
//
// THE STAGE FORMS ARE TODAY'S, MOUNTED AS THEY ARE: PaySellerInline, DeliverInline, VoidInline, BoxesNow and
// CustomerStillOwes are imported from McDeal.tsx, not copied. They hold the held cheque press (lib/chequePress.ts, P-042:
// a press written down before it is sent, sent again unchanged, a payment the books hold but never counted finished from
// the ledger's own figures, a started delivery finished with its own figures) and six review rounds of rules pinned by
// tests/cheque-press — one copy of them, so a fix there is a fix here. This page decides only what is shown around them,
// in what order. The two page-level effects of McDeal (a held press is dropped once its cheque has moved on; a kept seller
// press the books already count is settled with no tap) are copied verbatim below, before the early returns, as there.
//
// What it does NOT do (deliberate differences from Astra's mockup):
//   - no "Seller balance" figure in the strip: what the seller is still owed depends on the seller fee and each leg's
//     rate, and stage 2's own meter is the one place that works it out; the strip shows what the row holds — what the
//     seller has been paid so far, AED-equivalent.
//   - the current stage's gold button opens its form IN PLACE ("Record payment", "Record delivery"), and the form opens
//     by itself when something is waiting in it — a held press (lib/chequePress.ts), a payment the books hold that the
//     meter never counted, a delivery already started — so nothing that needs a person is folded away.
//   - one line above the stages while money still moves (stages 2 and 3): what the customer still owes, with the
//     operator's money-in-before-money-out door (10 Sept 2026) and the working behind Details. A balance that could not
//     be read is said in full, never folded. Astra has no such line.
//   - stage 1's line carries no figure: the strip holds the deposit (McDeal's 21 Oct rule — a done stage says where and
//     when, not a figure said above it).
//   - a voided cheque keeps its stage 1 line and says Voided in place of stages 2 and 3, with the reason.
import { Children, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { api } from '../../lib/api';
import { readWords } from '../../lib/errors';
import { askForCheque, countedSettles, dealKey, presses, useHeld, type SellerLegBody } from '../../lib/chequePress';
import { displayAsset, fmtAmount, fmtRelative } from '../../lib/format';
import { partyName, railName, sameAsset, shortReference } from '../../lib/naming';
import { boxParts } from '../../components/BoxLines';
import { CopyRef } from '../../components/CopyRef';
import { MoreDetails } from '../../components/MoreDetails';
import { Notice, PageHead, Panel, SummaryStrip, StateLine, ReadFailed, Pending, TAP } from '../../components/v2/kit';
import { DeliverInline, NO_BOX, PaySellerInline, VoidInline, stillToReceive, useBoxes } from '../McDeal';

type StageState = 'done' | 'current' | 'waiting' | 'voided';

/** One stage of the timeline: its mark, its name, one muted line, and — for the current stage only — its form below. */
function Stage({ n, state, title, sub, children }: { n: number; state: StageState; title: string; sub?: ReactNode; children?: ReactNode }) {
  const mark = {
    done: 'border-stroke text-tg-hint',
    current: 'border-tg-accent bg-tg-accent/10 text-tg-accent',
    waiting: 'border-stroke text-tg-hint',
    voided: 'border-neg/40 bg-neg-bg text-neg',
  }[state];
  return (
    <section className="border-b border-stroke-soft px-4 py-4 last:border-b-0 lg:px-5" aria-current={state === 'current' ? 'step' : undefined}>
      <div className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-3">
        <span className={`grid h-7 w-7 place-items-center rounded-full border text-meta font-medium ${mark}`} aria-hidden>
          {state === 'done' ? <Check className="h-3.5 w-3.5" /> : state === 'voided' ? <X className="h-3.5 w-3.5" /> : n}
        </span>
        <div className="min-w-0">
          <h3 className="text-body font-medium">{title}</h3>
          {sub && <p className="mt-0.5 text-meta text-tg-hint break-words">{sub}</p>}
        </div>
        {state === 'done' && <span className="pill text-pos bg-pos-bg">Done</span>}
        {state === 'waiting' && <span className="pill text-tg-hint bg-panel-3">Waiting</span>}
        {state === 'voided' && <span className="pill text-neg bg-neg-bg">Voided</span>}
      </div>
      {/* [false, false] is truthy: a Done or Waiting stage passes only conditions, and an empty body is 16 px of nothing. */}
      {Children.toArray(children).length > 0 && <div className="mt-4 lg:ml-10">{children}</div>}
    </section>
  );
}

/** A party's balances in plain words (no "square", no "holds"): signed as the books keep them — positive, they owe the desk;
 *  negative, the desk has theirs. Every asset is listed, each against its OWN dust (boxParts / dustFor — the rule today's
 *  "Balances now" used, so 0.004 BTC still shows); nothing open says so. */
export function balanceWords(balances: Record<string, number>): string {
  const parts = Object.entries(balances).flatMap(([a, v]) => {
    const { words, figure } = boxParts(Number(v), a);
    if (!figure) return [];
    return [words === 'owes you' ? `owes us ${figure} ${displayAsset(a)}` : `has ${figure} ${displayAsset(a)} with us`];
  });
  return parts.length ? parts.join(' · ') : 'nothing open';
}

/** WHAT THE CUSTOMER STILL OWES, IN ONE LINE (Screens' audit, 27 Oct 2026: "Balances now" folded to one line, no jargon).
 *  The figure is McDeal's (stillToReceive — one copy); behind Details, the working and EVERY balance today's "Balances now"
 *  showed — the customer's and the seller's, each asset — in plain words; the door carries the figure into Record money, as
 *  today's "Take customer money in" does. A balance not read is said in full — never a figure. A coin cheque's remainder
 *  depends on the handover rate, so it claims no AED still owed: it names the cheque and fee, and the coins already held. */
function OwesLine({ customerSlug, sellerSlug, custAsset, owedAed, href }: { customerSlug: string; sellerSlug: string; custAsset: string; owedAed: number; href: string }) {
  const { boxes: [box = NO_BOX, sellerBox = NO_BOX], failed, waiting, why, retry } = useBoxes([customerSlug, sellerSlug]);
  if (failed) return <ReadFailed what="the customer's and seller's balances" error={why} onRetry={retry} />;
  if (waiting) return <Pending what="Reading the customer's and seller's balances…" inline />;
  const { held, still } = stillToReceive(box, custAsset, owedAed);
  const door = (amount: number | null) => (
    <Link className={TAP} to={href + (amount && amount > 0 ? `&amount=${encodeURIComponent(String(amount))}` : '')}>Take money in →</Link>
  );
  const balances = (
    <>
      <span className="block">Customer: {balanceWords(box.balances)}</span>
      <span className="block">Seller: {balanceWords(sellerBox.balances)}</span>
    </>
  );
  if (custAsset !== 'AED') {
    return (
      <StateLine
        more={<><span className="block">The rest is paid in {displayAsset(custAsset)} at the rate agreed at handover.</span>{balances}</>}
        action={door(null)}
      >
        <OweFigure label="Cheque and fee" value={owedAed} />
      </StateLine>
    );
  }
  const paid = held ?? 0;
  const working = (
    <>
      <span className="block">Cheque and fee {fmtAmount(owedAed, 'AED')} AED · {paid >= 0 ? <>paid in so far {fmtAmount(paid, 'AED')} AED</> : <>their account already owed {fmtAmount(-paid, 'AED')} AED</>}</span>
      {balances}
    </>
  );
  if (!still) return <StateLine tone="good" more={working}>Customer has paid in full.</StateLine>;
  return (
    <StateLine more={working} action={door(still)}>
      {/* Says what the figure counts (1 Nov 2026 audit: under a 4,750,000 cheque a bigger figure read as this cheque's): the
          cheque and fee less what they paid in — their account's balance is part of it, the working line names both. */}
      <OweFigure label="Still to receive · this cheque and their account" value={still} />
    </StateLine>
  );
}

/** The line's label at the left and its figure at the right, mono, the unit muted (1 Nov 2026 audit: the figure sat inside
 *  a sentence between the cards and the stages). */
function OweFigure({ label, value }: { label: string; value: number }) {
  return (
    <span className="flex w-full items-baseline justify-between gap-3">
      <span>{label}</span>
      <span className="whitespace-nowrap"><span className="font-mono tabular text-tg-text">{fmtAmount(value, 'AED')}</span>&nbsp;<span className="text-meta text-tg-hint">AED</span></span>
    </span>
  );
}

export function ChequeProgressV2() {
  const { dealId } = useParams<{ dealId: string }>();
  const nav = useNavigate();
  const qc  = useQueryClient();

  // ── McDeal's reads and page-level effects, verbatim ──────────────────────────────────────────────────────────────
  const q = useQuery({
    queryKey: dealKey(dealId ?? ''),
    queryFn:  () => api.getMcDeal(dealId!),
    enabled:  !!dealId,
  });
  const treasury = useQuery({ queryKey: ['treasury', 'balances'], queryFn: () => api.treasuryBalances('dubaiotc') });
  // A held press is over once its cheque has moved on (the seller paid, the delivery done, voided): its entry on the disk goes with it (lib/chequePress.ts).
  const stageNow = q.data?.deal.lifecycle;
  useEffect(() => {
    if (!dealId || !stageNow) return;
    if (stageNow !== 'mc_pending_seller') presses.drop('seller-leg', dealId);
    if (stageNow !== 'mc_seller_paid') presses.drop('delivery', dealId);
  }, [dealId, stageNow]);

  // A kept seller press the cheque's meter already COUNTS is settled — no tap, no request (McDeal: mc_counted_legs). Read live (useHeld).
  const countedNow = q.data?.deal.mc_counted_legs;
  const sellerPress = useHeld<SellerLegBody>('seller-leg', dealId ?? '');
  useEffect(() => {
    if (!dealId || !countedNow) return;
    if (countedSettles(sellerPress, countedNow)) presses.answered('seller-leg', dealId, 'done', sellerPress!.body);
  }, [dealId, countedNow, sellerPress]);

  // The current stage's form opens on its gold button — or by itself when something in it waits for a person.
  const deliveryPress = useHeld<unknown>('delivery', dealId ?? '');
  const [opened, setOpened] = useState(false);
  // A cheque that moves on closes the form it was opened for: the next stage waits for its own gold button.
  useEffect(() => { setOpened(false); }, [stageNow]);

  const back = <button type="button" onClick={() => nav(-1)} className={`${TAP} mb-2 inline-block`}>← Back</button>;
  const justBooked = (useLocation().state as { justBooked?: string } | null)?.justBooked;

  // PENDING, not isLoading: a first read opened OFFLINE is paused — no data, not fetching — and is still waiting.
  if (q.isPending) return <>{back}<PageHead title="Cheque progress" /><Pending /></>;
  // Only replace the page when there is NOTHING to show; a refresh that fails keeps the stage forms and their typing.
  if (q.isError && !q.data) {
    return (
      <>
        {back}
        <PageHead title="Cheque progress" />
        {/* Straight after booking it here, the booking is said first — it posted, whatever this read does. */}
        {justBooked === dealId && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-stroke bg-panel px-4 py-3 text-body">
            <span className="flex items-center gap-2 font-medium text-pos"><Check className="h-4 w-4" aria-hidden />Cheque booked</span>
            <CopyRef value={dealId ?? ''} label={shortReference(dealId ?? '')} />
          </div>
        )}
        {/* A page with nothing else on it says its failure as a notice, with its way out (1 Nov 2026 audit, P5). */}
        <Notice tone="bad"><ReadFailed what="this cheque" error={q.error} onRetry={() => void q.refetch()} /></Notice>
      </>
    );
  }

  const d = q.data!.deal;
  const lifecycle       = d.lifecycle;
  const isVoided        = lifecycle === 'voided';
  const isPendingSeller = lifecycle === 'mc_pending_seller';
  const isSellerPaid    = lifecycle === 'mc_seller_paid';
  const isSettled       = lifecycle === 'settled';

  const face        = Number(d.mc_face_amount);
  const custFeeBps  = Number(d.mc_customer_fee_bps);
  const selFeeBps   = Number(d.mc_seller_fee_bps);
  const depAmt      = Number(d.mc_deposit_amount);
  const customerFee = face * custFeeBps / 10000;
  const sellerFee   = face * selFeeBps  / 10000;
  const spread      = customerFee - sellerFee;
  // The balance carries its own asset (McDeal: the route reads it back off the delivery's audit row; older deals fall back to the booking asset).
  const balanceAsset = (d as typeof d & { mc_balance_asset?: string | null }).mc_balance_asset ?? d.asset_take;
  const sellerPaid  = Number(d.mc_seller_pay_amount ?? 0);

  const refresh = () => qc.invalidateQueries({ queryKey: ['mc'] });
  const ask = () => void askForCheque(qc, dealId ?? '');
  const customer = partyName({ display_name: d.customer_name, slug: d.customer_slug });
  const seller   = partyName({ display_name: d.mc_seller_display_name, slug: d.mc_seller_slug });
  const railRows = treasury.data?.rows ?? [];
  const accountName = (slug: string | null | undefined, asset?: string) =>
    railName((slug ? railRows.find((r) => r.slug === slug && (!asset || sameAsset(r.asset, asset))) : undefined) ?? { slug });
  const pct = (bps: number) => `${(bps / 100).toFixed(2)}%`;
  const fig = (v: number | string, asset: string) => <>{fmtAmount(v, asset)} <span className="text-meta font-normal text-tg-hint">{displayAsset(asset)}</span></>;

  // ── the strip: what the row holds, never a figure worked out here ─────────────────────────────────────────────────
  // Astra's quantity: the figures and their labels; the fees are under More, the seller's figure says it is AED-equivalent.
  const strip = isSettled
    ? [
        { label: 'Deposit', value: fig(depAmt, d.asset_take) },
        { label: 'Balance', value: fig(Number(d.mc_balance_amount), balanceAsset) },
        { label: 'Seller paid', value: fig(sellerPaid, 'AED'), sub: 'AED-equivalent' },
        { label: 'Spread', value: fig(d.spread_amount ?? spread, 'AED') },
      ]
    : [
        { label: 'Face amount', value: fig(face, 'AED') },
        { label: 'Deposit received', value: fig(depAmt, d.asset_take) },
        { label: 'Seller paid', value: fig(sellerPaid, 'AED'), sub: 'AED-equivalent, so far' },
      ];
  const waitsForPerson = (isPendingSeller && (!!sellerPress || (d.mc_uncounted_legs ?? []).length > 0))
    || (isSellerPaid && (!!deliveryPress || !!d.mc_delivery_started));
  const formOpen = opened || waitsForPerson;
  const goldOpen = (words: string) => (
    // The stage's one gold button at the stage's right from lg, as every primary sits (1 Nov 2026 audit).
    <div className="flex lg:justify-end"><button type="button" className="btn-primary w-full px-4 lg:w-auto" onClick={() => setOpened(true)}>{words}</button></div>
  );

  // The seller's done line says only what the row knows: the meter is AED-equivalent, the rail is the LAST leg's (McDeal).
  const sellerDone = (
    <>
      {d.mc_seller_pay_treasury_slug ? <>last leg from {accountName(d.mc_seller_pay_treasury_slug)}</> : seller}
      {d.mc_seller_paid_at ? <> · {fmtRelative(d.mc_seller_paid_at)}</> : null}
    </>
  );

  return (
    <div className="lg:max-w-[960px]">
      {back}
      {q.isError && (
        <div className="mb-4">
          <StateLine
            tone="warn"
            more={readWords(q.error)}
            action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}
          >
            Books didn&apos;t answer — showing the last read.
          </StateLine>
        </div>
      )}

      <PageHead title="Cheque progress" />
      {/* Who and which, one fact each (1 Nov 2026 audit: customer, seller and reference were one subtitle). */}
      <dl className="-mt-2 mb-4 grid grid-cols-1 gap-x-8 gap-y-2 text-body sm:grid-cols-3 lg:mb-6">
        <div className="min-w-0"><dt className="text-meta text-tg-hint">Customer</dt><dd className="break-words">{customer}</dd></div>
        <div className="min-w-0"><dt className="text-meta text-tg-hint">Seller</dt><dd className="break-words">{seller}</dd></div>
        <div className="min-w-0"><dt className="text-meta text-tg-hint">Reference</dt><dd><CopyRef value={d.deal_id} label={shortReference(d.deal_id)} /></dd></div>
      </dl>

      <SummaryStrip items={strip} cols={strip.length === 4 ? 4 : 3} phoneCols={2} />

      {/* Money in before money out: what the customer still has to pay, in one line, while money still moves. */}
      {(isPendingSeller || isSellerPaid) && (
        <div className="mb-4 lg:mb-6">
          <OwesLine
            customerSlug={d.customer_slug}
            sellerSlug={d.mc_seller_slug}
            custAsset={d.asset_take}
            owedAed={face + customerFee}
            href={`/record?party=${encodeURIComponent(d.customer_slug)}&dir=in`
              + (d.mc_customer_treasury_slug ? `&rail=${encodeURIComponent(d.mc_customer_treasury_slug)}&railAsset=${encodeURIComponent(d.asset_take)}` : `&asset=${encodeURIComponent(d.asset_take)}`)
              + `&note=${encodeURIComponent('cheque ' + d.deal_id)}`}
          />
        </div>
      )}

      <Panel title="Stages" flush className="mb-4 lg:mb-6">
        <Stage
          n={1}
          state="done"
          title="Deposit booked"
          sub={<>into {accountName(d.mc_customer_treasury_slug, d.asset_take)} · {fmtRelative(d.booked_at)}</>}
        />
        {isVoided ? (
          <Stage
            n={2}
            state="voided"
            title="Voided"
            sub={<>{d.void_reason ?? ''}{d.voided_at && <> · {fmtRelative(d.voided_at)}</>}</>}
          />
        ) : (
          <>
            <Stage
              n={2}
              state={isPendingSeller ? 'current' : 'done'}
              title="Pay seller"
              sub={isPendingSeller ? seller : sellerDone}
            >
              {isPendingSeller && !formOpen && goldOpen('Record payment')}
              {isPendingSeller && formOpen && (
                <PaySellerInline
                  key={d.deal_id}
                  dealId={d.deal_id}
                  face={face}
                  defaultFeeBps={selFeeBps}
                  paidSoFar={sellerPaid}
                  sellerSlug={d.mc_seller_slug}
                  uncounted={d.mc_uncounted_legs ?? []}
                  readTick={q.data?.readTick ?? 0}
                  reading={q.isFetching}
                  readError={q.isError ? q.error : null}
                  onRetry={() => void q.refetch()}
                  onAsk={ask}
                  onDone={refresh}
                />
              )}
            </Stage>
            <Stage
              n={3}
              state={isSettled ? 'done' : isSellerPaid ? 'current' : 'waiting'}
              title="Deliver to customer"
              sub={isSettled
                ? (d.mc_delivered_at ? fmtRelative(d.mc_delivered_at) : null)
                : isSellerPaid ? <>{customer} pays the rest and takes the cheque</> : 'After the seller is paid'}
            >
              {isSellerPaid && !formOpen && goldOpen('Record delivery')}
              {isSellerPaid && formOpen && (
                <DeliverInline
                  key={d.deal_id}
                  dealId={d.deal_id}
                  face={face}
                  defaultFeeBps={custFeeBps}
                  deposit={depAmt}
                  custAsset={d.asset_take}
                  customerSlug={d.customer_slug}
                  started={d.mc_delivery_started ?? null}
                  readTick={q.data?.readTick ?? 0}
                  reading={q.isFetching}
                  readError={q.isError ? q.error : null}
                  onRetry={() => void q.refetch()}
                  onAsk={ask}
                  onDone={refresh}
                />
              )}
            </Stage>
          </>
        )}
      </Panel>

      {d.notes && (
        <Panel title="Notes" className="mb-4 lg:mb-6">
          <p className="text-body break-words">{d.notes}</p>
        </Panel>
      )}

      <MoreDetails>
        <div className="space-y-2 text-meta text-tg-hint">
          <p>
            Stage 2 pays the seller in legs — cash, coins at an agreed rate, more than one account — until the meter reads
            paid; a leg within a dirham of the figure closes the stage by itself. Stage 3 hands the cheque over: the customer
            pays whatever is still owed at handover — 0 if they already paid in full.
          </p>
          <p>
            Coins are valued at the rate you type, the pair&rsquo;s way (AED per USDT, INR per AED). Voiding marks the deal and
            moves nothing — what is posted stays posted. Give a deposit back from Record money; Adjust balance corrects the
            books and hands no money over.
          </p>
          <p>Face {fmtAmount(face, 'AED')} AED · customer fee {pct(custFeeBps)} · seller fee {pct(selFeeBps)}</p>
        </div>
        {/* A delivery charge on this cheque is EXTRA, on top of the cheque fee, and is its own entry (2 Oct 2026). */}
        <p className="text-meta">
          <Link className="btn-link max-lg:block max-lg:py-1" to={`/record?mode=charge&party=${encodeURIComponent(d.customer_slug)}&deal=${encodeURIComponent(d.deal_id)}`}>
            Add delivery charge →
          </Link>
        </p>
        {!isVoided && !isSettled && (
          <VoidInline
            dealId={d.deal_id}
            deposit={depAmt}
            depositAsset={d.asset_take}
            sellerPaidAed={sellerPaid}
            onDone={refresh}
          />
        )}
      </MoreDetails>
    </div>
  );
}
